import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Spinner, Modal } from "@/pages/admin/AdminCategories";

export default function AdminTags() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tags");
      setTags(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) return toast.error("Le nom est obligatoire");
    try {
      if (editing.id) {
        await api.put(`/admin/tags/${editing.id}`, { name });
        toast.success("Tag modifié");
      } else {
        await api.post("/admin/tags", { name });
        toast.success("Tag créé");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Supprimer le tag "${t.name}" ?`)) return;
    await api.delete(`/admin/tags/${t.id}`);
    toast.success("Tag supprimé");
    load();
  };

  const filtered = tags.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Tags</h1>
          <p className="text-sm text-slate-500">Métadonnées internes (non visibles par les joueurs).</p>
        </div>
        <button data-testid="add-tag-btn" onClick={() => { setEditing({}); setName(""); }} className="flex items-center gap-2 rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white active:scale-95">
          <Plus className="h-4 w-4" /> Nouveau
        </button>
      </div>

      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4">
        <Search className="h-4 w-4 text-slate-400" />
        <input data-testid="tag-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un tag..." className="flex-1 py-3 outline-none" />
      </div>

      {loading ? <Spinner /> : (
        <div className="flex flex-wrap gap-3" data-testid="tags-list">
          {filtered.map((t) => (
            <div key={t.id} className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-2 pl-4 pr-2">
              <span className="text-sm font-semibold">{t.name}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500">{t.question_count}</span>
              <button onClick={() => { setEditing(t); setName(t.name); }} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100"><Pencil className="h-3.5 w-3.5" /></button>
              <button onClick={() => remove(t)} className="rounded-full p-1.5 text-dh-red hover:bg-dh-red/10"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          ))}
          {filtered.length === 0 && <p className="w-full rounded-2xl border border-slate-200 bg-white p-6 text-center text-slate-400">Aucun tag.</p>}
        </div>
      )}

      {editing && (
        <Modal title={editing.id ? "Modifier le tag" : "Nouveau tag"} onClose={() => setEditing(null)}>
          <input data-testid="tag-name-input" autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && save()} placeholder="Nom du tag" className="w-full rounded-xl border border-slate-200 px-4 py-3 outline-none focus:border-dh-red" />
          <div className="mt-4 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold">Annuler</button>
            <button data-testid="save-tag-btn" onClick={save} className="rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white">Enregistrer</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
